import React, { useCallback, useState } from 'react';
import {
  FeedbackCommentBodyWithIndex,
  IMPACT_SUMMARY_QUESTION_ID,
  INDICATOR_DETAILS_QUESTION_ID,
  SelectedFeedback,
} from '../utils';
import { FeedbackCommentProps } from 'donor/portal/pages/FeedbackDashboard/Feedback/utils';
import {
  FeedbackApi,
  FeedbackStatus,
} from 'donor/portal/pages/FeedbackDashboard/api';
import CommentArea from '../CommentArea';
import MakeEditsModal from '../MakeEdits/MakeEditsModal';
import { RequestState } from 'shared/auth/hooks/useRequestState';
import { FeedbackQuestion, FeedbackQuestionsById } from '../MakeEdits/api';
import FeedbackImpactDetails from './FeedbackImpactDetails';
import RenderComments, { RenderCommentsProps } from './RenderComments';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import FeedbackVerificationButtons from './FeedbackVerificationButtons';
import ImpactSummaryDetails from './ImpactSummaryDetails';

interface FeedbackDetailsProps extends RenderCommentsProps {
  allowEdits: boolean;
  feedbackStatus: FeedbackStatus;
  reportYear?: string;
  report: FeedbackApi;
  feedbackByQuestionId: {
    [questionId: number]: FeedbackCommentBodyWithIndex[];
  };
  questions: FeedbackQuestionsById | undefined;
  requestState: RequestState<unknown>;
  loadingAnswers: boolean;
  reloadAnswers: () => unknown;
  markAsVerified: () => unknown;
  handleAnswerUpdate: (
    questionId: number,
    answer: string,
    questionText: string,
    questionType: FeedbackQuestion['questionType'],
  ) => unknown;
  handleAddComment: (
    selected: SelectedFeedback,
    comment: FeedbackCommentProps,
  ) => unknown;
}

const FeedbackDetails: React.FC<FeedbackDetailsProps> = ({
  report,
  reportId,
  reportYear,
  feedback,
  questions,
  programName,
  allowEdits,
  requestState,
  currentUserId,
  currentUserName,
  currentUserType,
  currentUserEmail,
  feedbackStatus,
  selectedSection,
  isSectionCompleted,
  feedbackByQuestionId,
  loadingAnswers,
  reloadAnswers,
  handleAnswerUpdate,
  handleAddComment,
  markAsVerified,
  deleteQuestionLevelComment,
  updateQuestionLevelComment,
  handleDeleteComment,
  handleUpdateComment,
}) => {
  const [openEditsModal, setOpenEditsModal] = useState<boolean>(false);

  const handleOpen = useCallback(() => {
    setOpenEditsModal(true);
  }, [setOpenEditsModal]);

  const handleClose = useCallback(() => {
    setOpenEditsModal(false);
  }, [setOpenEditsModal]);

  if (!questions) {
    return <TrueImpactLoading />;
  }

  if (
    !openEditsModal &&
    selectedSection.question?.questionId === INDICATOR_DETAILS_QUESTION_ID
  ) {
    return (
      <FeedbackImpactDetails
        reportId={reportId}
        feedback={feedback}
        programName={programName}
        isSectionCompleted={isSectionCompleted}
        answer={questions[INDICATOR_DETAILS_QUESTION_ID]?.answers[0]?.text}
        currentUserId={currentUserId}
        selectedSection={selectedSection}
        currentUserName={currentUserName}
        currentUserType={currentUserType}
        currentUserEmail={currentUserEmail}
        allowEdits={allowEdits}
        feedbackStatus={feedbackStatus}
        handleEditClick={handleOpen}
        handleVerifiedClick={markAsVerified}
        requestState={requestState}
        handleUpdateComment={handleUpdateComment}
        handleDeleteComment={handleDeleteComment}
        updateQuestionLevelComment={updateQuestionLevelComment}
        deleteQuestionLevelComment={deleteQuestionLevelComment}
      />
    );
  }

  if (
    !openEditsModal &&
    selectedSection.question?.questionId === IMPACT_SUMMARY_QUESTION_ID
  ) {
    return (
      <ImpactSummaryDetails
        reportId={reportId}
        feedback={feedback}
        programName={programName}
        isSectionCompleted={isSectionCompleted}
        answer={questions[IMPACT_SUMMARY_QUESTION_ID]?.answers[0]?.text}
        currentUserId={currentUserId}
        selectedSection={selectedSection}
        currentUserName={currentUserName}
        currentUserType={currentUserType}
        currentUserEmail={currentUserEmail}
        allowEdits={allowEdits}
        feedbackStatus={feedbackStatus}
        handleEditClick={handleOpen}
        handleVerifiedClick={markAsVerified}
        requestState={requestState}
        handleUpdateComment={handleUpdateComment}
        handleDeleteComment={handleDeleteComment}
        updateQuestionLevelComment={updateQuestionLevelComment}
        deleteQuestionLevelComment={deleteQuestionLevelComment}
      />
    );
  }

  return (
    <>
      <RenderComments
        reportId={reportId}
        feedback={feedback}
        programName={programName}
        currentUserId={currentUserId}
        selectedSection={selectedSection}
        currentUserName={currentUserName}
        currentUserType={currentUserType}
        currentUserEmail={currentUserEmail}
        isSectionCompleted={isSectionCompleted}
        handleUpdateComment={handleUpdateComment}
        handleDeleteComment={handleDeleteComment}
        updateQuestionLevelComment={updateQuestionLevelComment}
        deleteQuestionLevelComment={deleteQuestionLevelComment}
      />
      <FeedbackVerificationButtons
        allowEdits={allowEdits}
        feedbackStatus={feedbackStatus}
        handleEditClick={handleOpen}
        handleVerifiedClick={markAsVerified}
        requestState={requestState}
      />
      <CommentArea
        defaultOpenState={false}
        buttonText="Leave Additional Feedback"
        currentUserEmail={currentUserEmail}
        currentUserId={currentUserId}
        reportId={reportId}
        selectedSection={selectedSection}
        userName={currentUserName}
        userType={currentUserType}
        onSuccess={handleAddComment}
      />
      {selectedSection.question && (
        <MakeEditsModal
          report={report}
          isFinalizedUI={false}
          programName={programName}
          reportYear={reportYear}
          questions={questions || {}}
          isLoading={!questions}
          feedbackByQuestionId={feedbackByQuestionId}
          isError={requestState.type === 'REQUEST_ERROR'}
          open={openEditsModal}
          onClose={handleClose}
          feedback={feedback}
          isAnswersLoading={loadingAnswers}
          handleAnswerUpdate={handleAnswerUpdate}
          reloadAnswers={reloadAnswers}
          markAsCompleted={markAsVerified}
          selectedSection={{
            question: selectedSection.question,
            section: selectedSection.section,
          }}
          reportId={reportId}
        />
      )}
    </>
  );
};

export default FeedbackDetails;
